import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  UntypedFormControl,
} from '@angular/forms';
import { generateId } from '@common';
import { merge, Observable, of } from 'rxjs';
import { controlDisabledChanges } from '../../operators/control-disabled/control-disabled';
import { controlEnabledChanges } from '../../operators/control-enabled/control-enabled';
import { controlInvalidChanges } from '../../operators/control-invalid/control-invalid';
import { controlValidChanges } from '../../operators/control-valid/control-valid';
import {
  controlIsRequired,
  controlValueChanges$,
  markControlAsTouched,
} from '../../utils/form-control/form-control.utils';

@Component({
  selector: 'app-form-base-form-control',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class BaseFormControlComponent<T>
  implements ControlValueAccessor, OnInit
{
  protected readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly ngControl = inject(NgControl);

  control!: UntypedFormControl;
  id!: string;
  name!: string;
  required?: boolean;

  @Output() blurred = new EventEmitter<void>();
  @Output() focused = new EventEmitter<void>();

  constructor() {
    this.ngControl.valueAccessor = this as never;
  }

  ngOnInit(): void {
    this.loadControl();
  }

  registerOnChange = (callback: (value: T) => void): void => {
    this.onChangeCallback = callback;
  };

  registerOnTouched = (callback: () => void): void => {
    this.onTouchedCallback = callback;
  };

  setDisabledState = (): void => undefined;

  writeValue = (): void => undefined;

  detectChanges(): void {
    this.changeDetectorRef.detectChanges();
  }

  onBlur(): void {
    markControlAsTouched(this.control);
    this.blurred.emit();
  }

  onFocus(): void {
    this.focused.emit();
  }

  get disabled(): boolean {
    return this.control.disabled;
  }

  get enabled(): boolean {
    return this.control.enabled;
  }

  get disabledChanges(): Observable<boolean> {
    return of(this.control).pipe(controlDisabledChanges());
  }

  get enabledChanges(): Observable<boolean> {
    return of(this.control).pipe(controlEnabledChanges());
  }

  get valid(): boolean {
    return this.control.valid;
  }

  get invalid(): boolean {
    return this.control.invalid;
  }

  get validChanges(): Observable<boolean> {
    return of(this.control).pipe(controlValidChanges());
  }

  get invalidChanges(): Observable<boolean> {
    return of(this.control).pipe(controlInvalidChanges());
  }

  get statusChanges(): Observable<string> {
    return this.control.statusChanges;
  }

  get touched(): boolean {
    return this.control.touched;
  }

  get untouched(): boolean {
    return this.control.untouched;
  }

  get value(): T {
    return this.control.value;
  }

  get valueChanges(): Observable<T | undefined> {
    return controlValueChanges$<T>(this.control);
  }

  protected clear(): void {
    this.setValue();
  }

  protected setValue(value?: T): void {
    this.control.setValue(value);
  }

  private loadControl(): void {
    this.name ??= this.ngControl.name?.toString() ?? generateId();
    this.id = this.name;

    const control: AbstractControl | null = this.ngControl.control;

    if (!(control instanceof UntypedFormControl)) {
      console.error(
        'BaseFormControlComponent expects an instance of FormControl',
      );
      return;
    }

    this.control = control;
    this.required = controlIsRequired(this.control);
    this.listenToControlStateChanges();
  }

  private listenToControlStateChanges(): void {
    merge(this.disabledChanges, this.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });
  }

  private onChangeCallback: (value: T) => void = () => undefined;

  private onTouchedCallback: () => void = () => undefined;
}
